import { useEffect, useState } from "react";
import { Plus, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { WorkplaceMap } from "@/components/app/WorkplaceMap";
import { GeofenceControls } from "@/components/app/GeofenceControls";
import { api, formatError } from "@/lib/api";

export const useWorkplaces = () => {
  const [list, setList] = useState(null);
  const load = () => api.get("/workplaces").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => {
    load();
  }, []);
  return [list, load];
};

const Primary = (props) => <Button className="rounded-full bg-[#00C16B] px-6 hover:bg-[#00A85C]" {...props} />;

export const StepLocations = ({ policy, onNext }) => {
  const [list, load] = useWorkplaces();
  const [form, setForm] = useState({ name: "", address: "" });
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/workplaces", { name: form.name, address: form.address || null, geofence_radius_m: policy.default_radius_m });
      setForm({ name: "", address: "" });
      load();
    } catch (err) { toast.error(formatError(err)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/workplaces/${id}`); load(); } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <div className="space-y-5">
      <form onSubmit={add} className="grid gap-3 sm:grid-cols-[1fr_1.4fr_auto]" data-testid="add-location-form">
        <div className="space-y-1.5"><Label>Workplace name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Main store" className="h-11 rounded-xl" data-testid="location-name-input" /></div>
        <div className="space-y-1.5"><Label>Street address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="You'll pin it on the map next" className="h-11 rounded-xl" data-testid="location-address-input" /></div>
        <div className="flex items-end"><Button className="h-11 rounded-xl" variant="outline" data-testid="location-add-btn"><Plus className="mr-1 h-4 w-4" />Add</Button></div>
      </form>
      <ul className="space-y-2" data-testid="location-list">
        {(list || []).map((w) => (
          <li key={w.workplace_id} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3" data-testid={`location-item-${w.workplace_id}`}>
            <MapPin className="h-4 w-4 text-[#00A85C]" /><div className="min-w-0 flex-1"><p className="text-sm font-medium">{w.name}</p><p className="truncate text-xs text-slate-500">{w.address || "No address yet"}</p></div>
            <Button size="icon" variant="ghost" onClick={() => remove(w.workplace_id)} aria-label="Remove" data-testid={`location-remove-${w.workplace_id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
          </li>
        ))}
        {list?.length === 0 && <p className="text-sm text-slate-500">Add at least one workplace to continue.</p>}
      </ul>
      <Primary disabled={!list?.length} onClick={onNext} data-testid="step-continue-btn">Continue</Primary>
    </div>
  );
};

const WorkplaceTabs = ({ list, sel, setSel }) => (
  <div className="flex flex-wrap gap-2">
    {list.map((w) => (
      <button key={w.workplace_id} onClick={() => setSel(w.workplace_id)} data-testid={`wp-tab-${w.workplace_id}`}
        className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${sel === w.workplace_id ? "bg-[#0B2D5B] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
        {w.name}{w.lat != null && <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />}
      </button>
    ))}
  </div>
);

const useSelected = (list) => {
  const [sel, setSel] = useState(null);
  useEffect(() => {
    if (list?.length && !list.some((w) => w.workplace_id === sel)) { setSel(list[0].workplace_id); }
  }, [list, sel]);
  return [sel, setSel, list?.find((w) => w.workplace_id === sel)];
};

const WorkplaceEditorStep = ({ mode, onNext }) => {
  const [list, load] = useWorkplaces();
  const [sel, setSel, current] = useSelected(list);
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    setDraft(current ? { ...current } : null);
  }, [current]);
  const save = async () => {
    const body = mode === "map"
      ? { name: draft.name, lat: draft.lat, lng: draft.lng, address: draft.address, place_id: draft.place_id }
      : { name: draft.name, geofence_radius_m: draft.geofence_radius_m, location_verification: draft.location_verification, offsite_policy: draft.offsite_policy };
    try { await api.patch(`/workplaces/${draft.workplace_id}`, body); toast.success(`${draft.name} saved`); load(); } catch (err) { toast.error(formatError(err)); }
  };
  if (!list) return null;
  if (!list.length) return <p className="text-sm text-slate-500">Add a workplace first.</p>;
  return (
    <div className="space-y-5">
      <WorkplaceTabs list={list} sel={sel} setSel={setSel} />
      {draft && (mode === "map" ? (
        <WorkplaceMap value={draft} radius={draft.geofence_radius_m} onChange={(p) => setDraft((d) => ({ ...d, ...p }))} height={320} />
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          <GeofenceControls value={draft} onChange={(p) => setDraft((d) => ({ ...d, ...p }))} idPrefix="onb-geo" />
          {draft.lat != null ? <WorkplaceMap value={draft} radius={draft.geofence_radius_m} searchable={false} height={260} /> : <p className="rounded-xl bg-amber-50 p-4 text-sm text-amber-800">Pin this workplace on the map (previous step) to see its geofence.</p>}
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" className="rounded-full" onClick={save} disabled={mode === "map" && draft?.lat == null} data-testid="wp-save-btn">Save {draft?.name}</Button>
        <Primary onClick={onNext} data-testid="step-continue-btn">Continue</Primary>
      </div>
    </div>
  );
};

export const StepMap = (props) => <WorkplaceEditorStep mode="map" {...props} />;
export const StepGeofence = (props) => <WorkplaceEditorStep mode="geofence" {...props} />;
export { Primary };
